import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ThemeProvider } from './contexts/ThemeContext';
import { DataProvider } from './contexts/DataContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { CompanyMembershipProvider } from './contexts/CompanyMembershipContext';
import { CartProvider } from './contexts/CartContext';
import ErrorBoundary from './components/error/ErrorBoundary';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Schedule from './pages/Schedule';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import WorkDetail from './pages/WorkDetail';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import Layout from './components/Layout';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import CustomerForm from './components/customers/CustomerForm';
import EditCustomer from './pages/EditCustomer';
import Estimates from './pages/Estimates';
import EstimateDetail from './pages/EstimateDetail';
import EstimateForm from './components/estimates/EstimateForm';
import SignEstimate from './pages/SignEstimate';
import CustomerPortal from './pages/CustomerPortal';
import JobForm from './components/jobs/JobForm';
import Inventory from './pages/Inventory';
import InventoryDetail from './pages/InventoryDetail';
import InventoryCart from './pages/InventoryCart';
import CheckIn from './pages/CheckIn';
import DataDiagnostic from './pages/DataDiagnostic';
import OnboardingFlow from './components/onboarding/OnboardingFlow';
import RoleDiagnostic from './pages/RoleDiagnostic';
import Analytics from './pages/Analytics';

function PrivateRoute() {
  const { currentUser, loading } = useAuth();
  if (loading) return null;
  return currentUser ? <Outlet /> : <Navigate to="/login" replace />;
}

function AuthRedirect({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return null;
  return currentUser ? <Navigate to="/dashboard" replace /> : children;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <AuthProvider>
            <CompanyMembershipProvider>
              <CompanyProvider>
                <CartProvider>
                  <DataProvider>
                <Router>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={
                      <AuthRedirect>
                        <Landing />
                      </AuthRedirect>
                    } />
                    <Route path="/login" element={
                      <AuthRedirect>
                        <Login />
                      </AuthRedirect>
                    } />
                    <Route path="/signup" element={
                      <AuthRedirect>
                        <Signup />
                      </AuthRedirect>
                    } />
                    
                    {/* Public signing route - no auth required */}
                    <Route path="/sign-estimate/:estimateId/:token" element={<SignEstimate />} />
                    
                    {/* Public customer portal route - no auth required */}
                    <Route path="/portal/:portalToken" element={<CustomerPortal />} />

                    {/* Protected routes with sidebar/layout */}
                    <Route element={<PrivateRoute />}>
                      {/* Onboarding route - no layout/sidebar */}
                      <Route path="/onboarding" element={<OnboardingFlow />} />
                      
                      <Route element={<Layout />}>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/analytics" element={<Analytics />} />
                        <Route path="/schedule" element={<Schedule />} />
                        <Route path="/jobs" element={<Jobs />} />
                        <Route path="/jobs/new" element={<JobForm />} />
                        <Route path="/jobs/:jobId/edit" element={<JobForm />} />
                        <Route path="/jobs/:id" element={<JobDetail />} />
                        <Route path="/jobs/:jobId/work/:workItemId" element={<WorkDetail />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/admin" element={<Admin />} />
                        {/* Customers and Estimates routes */}
                        <Route path="/customers" element={<Customers />} />
                        <Route path="/customers/new" element={<CustomerForm />} />
                        <Route path="/customers/:id" element={<CustomerDetail />} />
                        <Route path="/customers/:id/edit" element={<EditCustomer />} />
                        <Route path="/estimates" element={<Estimates />} />
                        <Route path="/estimates/new" element={<EstimateForm />} />
                        <Route path="/estimates/:id" element={<EstimateDetail />} />
                        <Route path="/estimates/:id/edit" element={<EstimateForm />} />
                        <Route path="/inventory" element={<Inventory />} />
                        <Route path="/inventory/:id" element={<InventoryDetail />} />
                        <Route path="/inventory/pick-list" element={<InventoryCart />} />
                        <Route path="/inventory/check-in" element={<CheckIn />} />
                        <Route path="/diagnostic" element={<DataDiagnostic />} />
                        <Route path="/role-diagnostic" element={<RoleDiagnostic />} />
                      </Route>
                    </Route>

                    {/* Fallback: redirect unknown routes to / */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Router>
                  </DataProvider>
                </CartProvider>
              </CompanyProvider>
            </CompanyMembershipProvider>
          </AuthProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; 